/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { BookOpen, Coins, OctagonAlert, Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '../../api';
import type { AccountPosting, AccountType, BalanceHold, LedgerAccount, LedgerCurrencyTotals } from '../../api/areas/commerce';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, FilterBar, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { CorrelationLink, PlainId } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { useJournalDescription } from '../../components/SystemReason';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatCount, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import {
  ACCOUNT_STATUS,
  AccountOwner,
  AccountRef,
  Amount,
  cellLink,
  clearParams,
  FilterSelect,
  FilterText,
  HOLD_STATUS,
  HoldRef,
  isZero,
  JournalRef,
  JournalTable,
  Label,
  Pager,
  PostingsTable,
  RefreshButton,
  ResetFilters,
  ResourceRef,
  RichText,
  setParam,
  Status,
  useAccountName,
  useEnumLabel,
  useKeyset,
  When,
} from './shared';

const ACCOUNT_TYPES: AccountType[] = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];
const OWNER_TYPES = ['CUSTOMER', 'REGISTRY', 'PLATFORM'] as const;
/** Entry types the billing and ordering crates post today. */
const ENTRY_TYPES = ['WALLET_TOPUP', 'WALLET_ADJUSTMENT', 'WALLET_PAYOUT', 'HOLD_CAPTURE', 'REGISTRY_COST_ACCRUAL', 'REVERSAL'] as const;
const RESOURCE_TYPES = ['payment', 'balance_hold', 'order_item', 'journal_entry', 'customer'] as const;

/** Trial balance per currency: debits equal credits, and no single entry is unbalanced. */
function TrialBalance() {
  const { t } = useTranslation('commerce');
  const result = useApi((signal) => api.getLedgerSummary({ signal }), []);
  if (result.status === 'loading') return <Loading rows={3} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} title={t('ledger.trialLoadError')} />;
  const s = result.data;
  const columns: Column<LedgerCurrencyTotals>[] = [
    { key: 'currency', header: t('ledger.trialCol.currency'), cell: (c) => <code className="font-mono">{c.currency}</code> },
    { key: 'accounts', header: t('ledger.trialCol.accounts'), numeric: true, cell: (c) => formatCount(c.account_count) },
    { key: 'postings', header: t('ledger.trialCol.postings'), numeric: true, cell: (c) => formatCount(c.posting_count) },
    { key: 'debits', header: t('ledger.trialCol.debits'), numeric: true, cell: (c) => <Amount amount={c.debit_total} currency={c.currency} /> },
    { key: 'credits', header: t('ledger.trialCol.credits'), numeric: true, cell: (c) => <Amount amount={c.credit_total} currency={c.currency} /> },
    {
      key: 'difference',
      header: t('ledger.trialCol.difference'),
      numeric: true,
      cell: (c) => (
        <span className="inline-flex justify-end">
          {isZero(c.difference) ? (
            <Badge tone="ok">0</Badge>
          ) : (
            <Badge tone="crit" title={t('common.rawAmount', { value: c.difference })}>
              {c.difference}
            </Badge>
          )}
        </span>
      ),
    },
  ];
  return (
    <div className="flex flex-col gap-6">
      <StatGrid>
        <StatCard
          icon={Scale}
          label={t('ledger.trialBalance')}
          value={s.balanced ? t('ledger.balancedToZero') : t('ledger.imbalancedCount', { count: s.imbalanced_entries })}
          tone={s.balanced ? 'ok' : 'crit'}
          hint={t('ledger.trialHint')}
        />
        <StatCard icon={OctagonAlert} label={t('ledger.imbalancedEntries')} value={formatCount(s.imbalanced_entries)} tone={s.imbalanced_entries === 0 ? 'ok' : 'crit'} hint={t('ledger.mustBeZero')} />
        <StatCard icon={BookOpen} label={t('ledger.entriesPosted')} value={formatCount(s.journal_entry_count)} />
        <StatCard icon={Coins} label={t('ledger.currencies')} value={formatCount(s.currencies.length)} hint={s.currencies.map((c) => c.currency).join(', ') || undefined} />
      </StatGrid>
      <Section title={t('ledger.trialBalance')} flush>
        <DataTable columns={columns} rows={s.currencies} rowKey={(c) => c.currency} empty={<p className="text-sm text-muted-foreground">{t('ledger.noAccounts')}</p>} />
      </Section>
    </div>
  );
}

const ACCOUNT_FILTERS = ['owner_type', 'account_type', 'currency', 'q'] as const;

function AccountsTab() {
  const { t } = useTranslation('commerce');
  const [params, setParams] = useSearchParams();
  const owner = params.get('owner_type') ?? '';
  const type = (params.get('account_type') ?? '') as AccountType | '';
  const currency = params.get('currency') ?? '';
  const q = params.get('q') ?? '';
  const pager = useKeyset(`${owner}|${type}|${currency}|${q}`);
  const result = useApi(
    (signal) => api.listLedgerAccounts({ owner_type: owner || undefined, account_type: type || undefined, currency: currency || undefined, q: q || undefined, limit: 100, after: pager.before }, { signal }),
    [owner, type, currency, q, pager.before],
  );
  const columns: Column<LedgerAccount>[] = [
    { key: 'account', header: t('ledger.accountCol.account'), cell: (a) => <AccountRef id={a.id} code={a.code} ownerName={a.owner_name} currency={a.currency} /> },
    { key: 'type', header: t('ledger.accountCol.type'), cell: (a) => <Label group="accountType" value={a.account_type} /> },
    { key: 'owner', header: t('ledger.accountCol.owner'), cell: (a) => <AccountOwner account={a} /> },
    { key: 'debits', header: t('ledger.accountCol.debits'), numeric: true, cell: (a) => <Amount amount={a.debit_total} currency={a.currency} /> },
    { key: 'credits', header: t('ledger.accountCol.credits'), numeric: true, cell: (a) => <Amount amount={a.credit_total} currency={a.currency} /> },
    { key: 'balance', header: t('ledger.accountCol.balance'), numeric: true, cell: (a) => <Amount amount={a.balance} currency={a.currency} className="font-semibold" /> },
    { key: 'held', header: t('ledger.accountCol.held'), numeric: true, cell: (a) => (isZero(a.held) ? <span className="text-muted-foreground">0</span> : <Amount amount={a.held} currency={a.currency} />) },
    { key: 'available', header: t('ledger.accountCol.available'), numeric: true, cell: (a) => <Amount amount={a.available} currency={a.currency} /> },
    { key: 'status', header: t('ledger.accountCol.status'), cell: (a) => <Status group="accountStatus" tones={ACCOUNT_STATUS} value={a.status} /> },
  ];
  return (
    <>
      <FilterBar actions={<ResetFilters show={Boolean(owner || type || currency || q)} onReset={() => setParams(clearParams(params, ACCOUNT_FILTERS))} />}>
        <FilterText label={t('ledger.codeFilter')} value={q} onChange={(v) => setParams(setParam(params, 'q', v))} placeholder={t('ledger.codePlaceholder')} />
        <FilterSelect label={t('ledger.owner')} value={owner} options={OWNER_TYPES} group="ownerType" onChange={(v) => setParams(setParam(params, 'owner_type', v))} />
        <FilterSelect label={t('ledger.type')} value={type} options={ACCOUNT_TYPES} group="accountType" onChange={(v) => setParams(setParam(params, 'account_type', v))} />
        <FilterText label={t('ledger.currency')} value={currency} className="sm:w-28" onChange={(v) => setParams(setParam(params, 'currency', v.toUpperCase()))} placeholder="VND" />
      </FilterBar>
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('ledger.accountsLoadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable columns={columns} rows={result.data.items} rowKey={(a) => a.id} empty={<EmptyState title={t('ledger.accountsEmpty')} />} />
            {result.data.items.length > 0 ? <p className="border-t px-5 py-3 text-xs text-muted-foreground">{t('ledger.balanceNote')}</p> : null}
            <Pager cursors={pager.cursors} next={result.data.next_after} onNewer={pager.newer} onOlder={pager.older} />
          </>
        ) : null}
      </Section>
    </>
  );
}

function JournalTab() {
  const { t } = useTranslation('commerce');
  const [params, setParams] = useSearchParams();
  const entryType = params.get('entry_type') ?? '';
  const resourceType = params.get('resource_type') ?? '';
  const pager = useKeyset(`${entryType}|${resourceType}`);
  const result = useApi(
    (signal) => api.listJournalEntries({ entry_type: entryType || undefined, resource_type: resourceType || undefined, limit: 50, before: pager.before }, { signal }),
    [entryType, resourceType, pager.before],
  );
  return (
    <>
      <FilterBar actions={<ResetFilters show={Boolean(entryType || resourceType)} onReset={() => setParams(clearParams(params, ['entry_type', 'resource_type']))} />}>
        <FilterSelect label={t('ledger.entryType')} value={entryType} options={ENTRY_TYPES} group="entryType" className="sm:w-52" onChange={(v) => setParams(setParam(params, 'entry_type', v))} />
        <FilterSelect label={t('ledger.resource')} value={resourceType} options={RESOURCE_TYPES} group="resourceType" onChange={(v) => setParams(setParam(params, 'resource_type', v))} />
      </FilterBar>
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('ledger.journalLoadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <JournalTable entries={result.data.items} empty={<EmptyState title={t('ledger.journalEmpty')} />} />
            <Pager cursors={pager.cursors} next={result.data.next_before} onNewer={pager.newer} onOlder={pager.older} />
          </>
        ) : null}
      </Section>
    </>
  );
}

/** `/ledger`: the double-entry ledger — trial balance, accounts and journal. */
export function LedgerPage() {
  const { t } = useTranslation('commerce');
  useDocumentTitle(t('ledger.title'));
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab = requested === 'journal' || requested === 'balance' ? requested : 'accounts';
  const switchTab = (next: string) => setParams(next === 'accounts' ? new URLSearchParams() : new URLSearchParams({ tab: next }));
  return (
    <>
      <PageHeader title={t('ledger.title')} crumbs={[{ label: t('crumb') }]} subtitle={<RichText k="ledger.subtitle" />} />
      <div className="flex flex-col gap-6">
        <Tabs value={tab} onValueChange={switchTab}>
          <TabsList aria-label={t('ledger.view')}>
            <TabsTrigger value="accounts">{t('ledger.tab.accounts')}</TabsTrigger>
            <TabsTrigger value="journal">{t('ledger.tab.journal')}</TabsTrigger>
            <TabsTrigger value="balance">{t('ledger.trialBalance')}</TabsTrigger>
          </TabsList>
          <TabsContent value="accounts" className="pt-2">
            {tab === 'accounts' ? <AccountsTab /> : null}
          </TabsContent>
          <TabsContent value="journal" className="pt-2">
            {tab === 'journal' ? <JournalTab /> : null}
          </TabsContent>
          <TabsContent value="balance" className="pt-2">
            {tab === 'balance' ? <TrialBalance /> : null}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

/** `/ledger/accounts/:accountId`: one account with balances, postings and active holds. */
export function LedgerAccountPage() {
  const { t } = useTranslation('commerce');
  const accountName = useAccountName();
  const { accountId = '' } = useParams();
  useDocumentTitle(t('ledgerAccount.title'));
  const result = useApi((signal) => api.getLedgerAccount(accountId, { signal }), [accountId]);
  const crumbs = [{ label: t('crumb') }, { label: t('ledger.title'), to: '/ledger' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('ledgerAccount.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('ledgerAccount.loadError')} />
      </>
    );
  }
  const { account: a, postings, active_holds } = result.data;
  const name = accountName(a);
  const holdColumns: Column<BalanceHold>[] = [
    { key: 'hold', header: t('billing.holdCol.hold'), cell: (h) => <HoldRef hold={h} /> },
    { key: 'status', header: t('billing.holdCol.status'), cell: (h) => <Status group="holdStatus" tones={HOLD_STATUS} value={h.status} /> },
    { key: 'amount', header: t('billing.holdCol.amount'), numeric: true, cell: (h) => <Amount amount={h.amount} currency={h.currency} /> },
    { key: 'created', header: t('billing.holdCol.created'), cell: (h) => <When at={h.created_at} /> },
  ];
  const postingColumns: Column<AccountPosting>[] = [
    {
      key: 'description',
      header: t('common.description'),
      className: 'whitespace-normal',
      cell: (p) => (
        <span className="block min-w-48">
          <JournalRef id={p.journal_entry_id} description={p.description} entryType={p.entry_type} />
        </span>
      ),
    },
    { key: 'type', header: t('common.type'), cell: (p) => <Label group="entryType" value={p.entry_type} /> },
    { key: 'debit', header: t('common.debit'), numeric: true, cell: (p) => (p.direction === 'DEBIT' ? <Amount amount={p.amount} currency={p.currency} /> : null) },
    { key: 'credit', header: t('common.credit'), numeric: true, cell: (p) => (p.direction === 'CREDIT' ? <Amount amount={p.amount} currency={p.currency} /> : null) },
    { key: 'posted', header: t('common.posted'), cell: (p) => <When at={p.posted_at} /> },
  ];
  return (
    <>
      <PageHeader title={name.label} crumbs={[...crumbs, { label: t('ledgerAccount.crumb') }]} subtitle={name.sub || undefined} actions={<RefreshButton onClick={result.reload} />} />
      <div className="flex flex-col gap-6">
        <StatGrid className="xl:grid-cols-3">
          <StatCard label={t('ledgerAccount.stat.balance')} value={<Amount amount={a.balance} currency={a.currency} />} />
          <StatCard label={t('ledgerAccount.stat.held')} value={<Amount amount={a.held} currency={a.currency} />} tone={isZero(a.held) ? 'neutral' : 'pending'} />
          <StatCard label={t('ledgerAccount.stat.available')} value={<Amount amount={a.available} currency={a.currency} />} tone="ok" />
        </StatGrid>
        <Section>
          <KeyValue
            columns={3}
            rows={[
              [t('common.type'), <Label key="t" group="accountType" value={a.account_type} />],
              [t('common.currency'), <code key="c" className="font-mono">{a.currency}</code>],
              [t('common.status'), <Status key="s" group="accountStatus" tones={ACCOUNT_STATUS} value={a.status} />],
              [t('ledger.owner'), <AccountOwner key="o" account={a} />],
              [t('ledgerAccount.field.negative'), a.allow_negative ? t('ledgerAccount.field.allowed') : t('ledgerAccount.field.rejected')],
              [t('ledgerAccount.field.opened'), formatDateTime(a.created_at)],
              [t('ledgerAccount.field.debits'), <Amount key="d" amount={a.debit_total} currency={a.currency} />],
              [t('ledgerAccount.field.credits'), <Amount key="cr" amount={a.credit_total} currency={a.currency} />],
              [t('ledgerAccount.field.postings'), formatCount(a.posting_count)],
              [t('ledgerAccount.field.code'), <CopyText key="code" value={a.code} />],
            ]}
          />
        </Section>
        {active_holds.length ? (
          <Section title={t('ledgerAccount.activeHolds')} flush>
            <DataTable columns={holdColumns} rows={active_holds} rowKey={(h) => h.id} />
          </Section>
        ) : null}
        <Section
          title={t('ledgerAccount.postings')}
          flush
          actions={
            <Link to="/ledger?tab=journal" className={`${cellLink} text-sm`}>
              {t('ledgerAccount.allEntries')}
            </Link>
          }
        >
          <DataTable columns={postingColumns} rows={postings} rowKey={(p) => p.id} empty={<p className="text-sm text-muted-foreground">{t('ledgerAccount.noPostings')}</p>} />
        </Section>
      </div>
    </>
  );
}

/** `/ledger/journal/:entryId`: one immutable journal entry and its postings. */
export function JournalEntryPage() {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const words = useJournalDescription();
  const { entryId = '' } = useParams();
  useDocumentTitle(t('journal.title'));
  const result = useApi((signal) => api.getJournalEntry(entryId, { signal }), [entryId]);
  const crumbs = [{ label: t('crumb') }, { label: t('ledger.title'), to: '/ledger?tab=journal' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('journal.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('journal.loadError')} />
      </>
    );
  }
  const { entry: e, totals } = result.data;
  const balanced = totals.every((x) => x.debit === x.credit);
  return (
    <>
      <PageHeader title={words(e.description, e.entry_type).text} crumbs={[...crumbs, { label: t('journal.title') }]} subtitle={label('entryType', e.entry_type)} actions={<RefreshButton onClick={result.reload} />} />
      <div className="flex flex-col gap-6">
        <Section>
          <KeyValue
            columns={2}
            rows={[
              [t('journal.field.entryType'), <Label key="t" group="entryType" value={e.entry_type} />],
              [t('common.resource'), <ResourceRef key="r" entry={e} />],
              [t('journal.field.effective'), formatDateTime(e.effective_at)],
              [t('common.posted'), formatDateTime(e.posted_at)],
              [
                // A staff actor's id is their user id, so the name is what is shown; a system or
                // external actor's id is already the component's name.
                t('journal.field.actor'),
                <span key="a" title={e.actor_id}>
                  <Label group="actorType" value={e.actor_type} /> · <span className="text-xs">{e.actor_name ?? e.actor_id}</span>
                </span>,
              ],
              [t('journal.field.correlation'), <CorrelationLink key="c" id={e.correlation_id} />],
              [t('journal.field.reverses'), e.reverses_entry_id ? <JournalRef key="rv" id={e.reverses_entry_id} description={e.reverses_description} /> : null],
              [t('journal.field.reversedBy'), e.reversed_by_entry_id ? <JournalRef key="rb" id={e.reversed_by_entry_id} description={e.reversed_by_description} /> : null],
              [t('journal.field.approval'), <PlainId key="ap" id={e.approval_request_id} />],
              [t('journal.field.request'), <PlainId key="rq" id={e.request_id} />],
              [t('journal.field.id'), <CopyText key="id" value={e.id} />],
            ]}
          />
        </Section>
        <Section title={t('common.postings')} flush actions={balanced ? <Badge tone="ok">{t('journal.balanced')}</Badge> : <Badge tone="crit">{t('journal.unbalanced')}</Badge>}>
          <PostingsTable postings={e.postings} />
          <div className="border-t">
            {totals.map((x) => (
              <div key={x.currency} className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 px-4 py-2.5 text-sm font-semibold">
                <span>{t('journal.total', { currency: x.currency })}</span>
                <span className="flex gap-6">
                  <span>
                    <span className="mr-2 text-xs font-normal text-muted-foreground">{t('common.debit')}</span>
                    <Amount amount={x.debit} currency={x.currency} />
                  </span>
                  <span>
                    <span className="mr-2 text-xs font-normal text-muted-foreground">{t('common.credit')}</span>
                    <Amount amount={x.credit} currency={x.currency} />
                  </span>
                </span>
              </div>
            ))}
          </div>
        </Section>
        <Callout tone="info">{t('journal.immutable')}</Callout>
      </div>
    </>
  );
}
